import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  Signal,
  computed,
  effect,
  inject,
} from '@angular/core';
import { FLOATING_PANEL_DATA } from '@shared/floating-panel/floating-panel-data.token';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../bodygraph/hd-gate-info.service';
import { HdPanelSectionScroller } from '../bodygraph/hd-panel-section-scroller';
import { HdCircuitGateCardComponent } from '../circuit-gates/hd-circuit-gate-card.component';
import { HdCircuitGateSectionComponent } from '../circuit-gates/hd-circuit-gate-section.component';
import {
  HD_MYSTICAL_CIRCUIT_GATES,
  HD_MYSTICAL_GATE_COLOR,
  HD_MYSTICAL_ICON,
  HD_MYSTICAL_STAGES,
  HdMysticalPanelData,
  HdMysticalStage,
} from './hd-mystical.metadata';

interface MysticalStageVm {
  readonly stage: HdMysticalStage;
  readonly name: string;
  readonly keynote: string;
  readonly gates: readonly number[];
}

interface MysticalPanelVm {
  readonly title: string;
  readonly introTitle: string;
  readonly introParagraphs: readonly string[];
  readonly streamTitle: string;
  readonly stages: readonly MysticalStageVm[];
  readonly endingTitle: string;
  readonly endingText: string;
  readonly jumpHint: string;
  readonly linesHint: string;
  readonly source: string;
}

/**
 * Mystiek-paneel: bovenaan de algemene uitleg over het mystieke circuit, dan de
 * stroom in zijn vijf bewegingen (druk, aanraking, kracht, liefde, expressie)
 * met per beweging de poortblokjes (klikken springt naar de sectie), een korte
 * noot over het einde van het circuit (2027) en tot slot per poort een
 * {@link HdCircuitGateSectionComponent} met het mystieke aspect van die poort
 * en zijn zes lijnen (Rave Cosmology II, lezing 4 tot 9).
 * Geopend via `HdMysticalPanelService`; een `focusGate` in de paneel-data laat
 * het paneel na render naar die poort scrollen (ook bij een al open paneel).
 */
@Component({
  selector: 'hd-mystical-circuit-panel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdCircuitGateCardComponent, HdCircuitGateSectionComponent],
  templateUrl: './hd-mystical-circuit-panel.component.html',
  styleUrl: './hd-mystical-circuit-panel.component.scss',
})
export class HdMysticalCircuitPanelComponent implements AfterViewChecked {
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  private readonly panelData = inject(FLOATING_PANEL_DATA, { optional: true }) as
    | Signal<HdMysticalPanelData>
    | null;

  /** Auto-scroll naar de focus-poort, gedeeld met de andere thema-panelen. */
  private readonly scroller = new HdPanelSectionScroller(this.host.nativeElement, this.cdr);

  protected readonly icon = HD_MYSTICAL_ICON;
  protected readonly gates = HD_MYSTICAL_CIRCUIT_GATES;

  protected readonly vm = computed<MysticalPanelVm>(() => ({
    title: this.text('panelTitle'),
    introTitle: this.text('intro.title'),
    introParagraphs: this.list('intro.paragraphs'),
    streamTitle: this.text('stream.title'),
    stages: HD_MYSTICAL_STAGES.map(({ stage, gates }) => ({
      stage,
      name: this.text(`stream.stages.${stage}.name`),
      keynote: this.text(`stream.stages.${stage}.keynote`),
      gates,
    })),
    endingTitle: this.text('ending.title'),
    endingText: this.text('ending.text'),
    jumpHint: this.text('jumpHint'),
    linesHint: this.text('linesHint'),
    source: this.text('source'),
  }));

  constructor() {
    // Beide content-talen eager laden: taalwissel + NL-fallback zonder flikker.
    this.gateInfo.loadScopesForLang('nl');
    this.gateInfo.loadScopesForLang('en');

    // Elke (her)aanlevering van paneel-data met een focus-poort wapent de
    // auto-scroll opnieuw; het scrollen zelf gebeurt in ngAfterViewChecked
    // zodra de sectie gerenderd is en de scope-tekst geladen is.
    effect(() => {
      const focusGate = this.panelData?.().focusGate ?? null;
      if (focusGate != null) {
        this.scroller.arm(
          `[data-section-gate="${focusGate}"]`,
          () => this.text(`gates.${focusGate}.keynote`) !== '',
        );
      }
    });

    this.destroyRef.onDestroy(() => this.scroller.dispose());
  }

  ngAfterViewChecked(): void {
    this.scroller.onViewChecked();
  }

  /** Railkleur van het blokje (en de sectie) van deze poort. */
  protected railFor(gate: number): string | undefined {
    return HD_MYSTICAL_GATE_COLOR[gate];
  }

  protected scrollToGate(gate: number): void {
    this.scroller.jump(`[data-section-gate="${gate}"]`);
  }

  private text(key: string): string {
    const lang = this.contentLang.effectiveLang();
    const value = this.gateInfo.cheatSheetTextIn('mystical', key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetTextIn('mystical', key, 'nl');
  }

  private list(key: string): readonly string[] {
    const lang = this.contentLang.effectiveLang();
    const value = this.gateInfo.cheatSheetListIn('mystical', key, lang);
    if (value.length > 0 || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetListIn('mystical', key, 'nl');
  }
}
